import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { RadioButton } from 'lego-on-react';
import * as React from 'react';
import { ChangeEvent } from 'react';

import { ChangeFeedFilterRatePayload } from '../../actions/feed-filters/rate-form';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { Bem } from '../../service/Bem/Bem';
import { TestBem } from '../../service/TestBem';
import { ValidationErrorData } from '../../typings/common';
import { FeedFilterRateType } from '../../typings/feedFilters';
import { CurrencyCode } from '../../typings/graphql-grid';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ErrorText } from '../ErrorText/ErrorText';
import { NumberInput } from '../NumberInput/NumberInput';

import './FeedFilterRateForm.css';
import * as keyset from './FeedFilterRateForm.i18n';

export interface FeedFilterRateFormProps {
    currency: CurrencyCode;
    isLoading: boolean;
    rateType: FeedFilterRateType;
    hasNote?: boolean;
    fixedRateType?: FeedFilterRateType;
    rate?: number;
    rateErrors?: Array<ValidationErrorData>;
    onCancel: () => void;
    onRateChange: (payload: ChangeFeedFilterRatePayload) => void;
    onSave: (rateType: FeedFilterRateType) => void;
}

const rateTypes = {
    [FeedFilterRateType.cpc]: 'CPC',
    [FeedFilterRateType.cpa]: 'CPA'
};

const BLOCK = 'feed-filter-rate-form';

const i18n = i18nFactory(keyset);

export class FeedFilterRateForm extends React.PureComponent<FeedFilterRateFormProps> {
    render() {
        const { currency, fixedRateType, rateErrors, rate, isLoading, onCancel } = this.props;

        const rateTypeLabel = fixedRateType ? rateTypes[fixedRateType] : '';

        return (
            <div className={Bem.cls(BLOCK)}>
                {this.renderNote()}
                <div className={Bem.cls(BLOCK, 'controls')}>
                    <span className={Bem.cls(BLOCK, 'rate-label')}>
                        {i18n('rate-label', { rateType: rateTypeLabel })}
                    </span>
                    {!fixedRateType && this.renderRateTypesRadioButtons()}
                    <NumberInput
                        theme="normal"
                        size="n"
                        value={rate}
                        valid={!rateErrors || rateErrors.length === 0}
                        errorText={<ErrorText errors={rateErrors} />}
                        cls={classNames(Bem.cls(BLOCK, 'rate-control'), TestBem.cls(BLOCK, 'rate-control'))}
                        onValueChange={this.onRateChange}
                    />
                    <span className={Bem.cls(BLOCK, 'currency-symbol')}>{mapStringToCurrencySymbol(currency)}</span>
                </div>
                <ButtonDecorator
                    progress={isLoading}
                    cls={classNames(Bem.cls(BLOCK, 'apply-button'), TestBem.cls(BLOCK, 'apply-button'))}
                    theme="action"
                    view="default"
                    size="n"
                    tone="default"
                    text={i18n('assign')}
                    onClick={this.onSave}
                />
                <ButtonDecorator
                    tone="default"
                    view="default"
                    size="n"
                    theme="normal"
                    text={i18n('cancel')}
                    onClick={onCancel}
                />
            </div>
        );
    }

    private renderRateTypesRadioButtons = () => {
        const { rateType } = this.props;

        return (
            <RadioButton
                freeWidth={true}
                theme="normal"
                view="default"
                value={rateType}
                size="n"
                cls={Bem.cls(BLOCK, 'rate-type-control')}
                onChange={this.onRateTypeChange}
            >
                {Object.keys(rateTypes).map(key => (
                    <RadioButton.Radio key={key} value={key}>
                        {rateTypes[key]}
                    </RadioButton.Radio>
                ))}
            </RadioButton>
        );
    };

    private renderNote = () => {
        const { hasNote, rateType } = this.props;

        if (!hasNote) {
            return null;
        }

        const note = rateType === FeedFilterRateType.cpc ? i18n('cpc-strategy-note') : i18n('cpa-strategy-note');

        return <div className={Bem.cls(BLOCK, 'note')}>{note}</div>;
    };

    private onRateTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
        this.props.onRateChange({
            rateType: event.target.value as FeedFilterRateType
        });
    };

    private onRateChange = (rate?: number) => {
        this.props.onRateChange({ rate });
    };

    private onSave = () => {
        const { rateType, fixedRateType, onSave } = this.props;

        onSave(fixedRateType || rateType);
    };
}
